import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage as FM } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { withTheme } from 'common/styling/theme';
import { PageContentPreloader, BaseDateRangeField } from 'common/components';

import { getUserTotalPayments } from 'backoffice/users/_redux';

import { staticStyles, getDynamicStyles } from './style';

const UserTotalPayments = ({ getUserTotalPayments, totalPayments, loadingTotalPayments, theme }) => {
  const { id } = useParams();
  const location = useLocation();

  useEffect(() => {
    getUserTotalPayments(id);
  }, [getUserTotalPayments]);

  const [dateValue, setDateValue] = useState('');
  const query = useMemo(() => queryString.parse(location.search), [location.search]);

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const rows = useMemo(
    () =>
      Object.keys(totalPayments).length
        ? [
            {
              rowTextId: 'boLifeTime',
              deposits: totalPayments.deposits.total,
              withdrawals: totalPayments.withdrawals.total,
              commissions: totalPayments.commissions.total,
              bonuses: totalPayments.bonuses?.total,
            },
            {
              rowTextId: 'justLastYear',
              deposits: totalPayments.deposits.lastYear,
              withdrawals: totalPayments.withdrawals.lastYear,
              commissions: totalPayments.commissions.lastYear,
              bonuses: totalPayments.bonuses?.lastYear,
            },
            {
              rowTextId: 'justLastSixMonths',
              deposits: totalPayments.deposits.lastSixMonths,
              withdrawals: totalPayments.withdrawals.lastSixMonths,
              commissions: totalPayments.commissions.lastSixMonths,
              bonuses: totalPayments.bonuses?.lastSixMonths,
            },
          ]
        : [],
    [totalPayments]
  );

  const periodRow = useMemo(() =>
    Object.keys(totalPayments).length
      ? {
          rowTextId: 'justPeriod',
          deposits: totalPayments.deposits.total_in_period,
          withdrawals: totalPayments.withdrawals.total_in_period,
          commissions: totalPayments.commissions.total_in_period,
          bonuses: totalPayments.bonuses?.total_in_period,
        }
      : null
  );

  const handleDateChange = date => {
    const [startDate, endDate] = date.split('_');

    query.from_date = startDate;
    query.to_date = endDate;

    setDateValue(`${startDate}_${endDate}`);
    getUserTotalPayments(id, query);
  };

  const handleDateClear = () => {
    getUserTotalPayments(id);
    setDateValue('');
  };

  return (
    <PageContentPreloader ready={loadingTotalPayments} type="smallLines" firstLaunchOnly>
      <div className="UserTotalPayments__dateField">
        <BaseDateRangeField
          placeholderId="justDate"
          value={dateValue}
          onChange={handleDateChange}
          onClear={handleDateClear}
        />
      </div>
      <div className="UserTotalPayments__Table">
        <div className="UserTotalPayments__Table__row UserTotalPayments__Table__row--header">
          <div className="UserTotalPayments__Table__col">
            <FM id="justTime" />
          </div>
          <div className="UserTotalPayments__Table__col">
            <FM id="justDepositsTotal" />
          </div>
          <div className="UserTotalPayments__Table__col">
            <FM id="justWithdrawalsTotal" />
          </div>
          <div className="UserTotalPayments__Table__col">
            <FM id="justCommissionsTotal" />
          </div>
          <div className="UserTotalPayments__Table__col">
            <FM id="boBonuses" />
          </div>
        </div>
        {!dateValue &&
          rows.map((row, index) => (
            <div className="UserTotalPayments__Table__row" key={index}>
              <div className="UserTotalPayments__Table__col">
                <FM id={row.rowTextId} />
              </div>
              <div className="UserTotalPayments__Table__col">
                {row.deposits ? `${row.deposits.toFixed(2)} USD` : '---'}
              </div>
              <div className="UserTotalPayments__Table__col">
                {row.withdrawals ? `${row.withdrawals.toFixed(2)} USD` : '---'}
              </div>
              <div className="UserTotalPayments__Table__col">
                {row.commissions ? `${row.commissions.toFixed(2)} USD` : '---'}
              </div>
              <div className="UserTotalPayments__Table__col">{`${(row.bonuses || 0).toFixed(2)} USD`}</div>
            </div>
          ))}
        {dateValue && (
          <div className="UserTotalPayments__Table__row">
            <div className="UserTotalPayments__Table__col">
              <FM id={periodRow.rowTextId} />
            </div>
            <div className="UserTotalPayments__Table__col">
              {periodRow.deposits ? `${periodRow.deposits.toFixed(2)} USD` : '---'}
            </div>
            <div className="UserTotalPayments__Table__col">
              {periodRow.withdrawals ? `${periodRow.withdrawals.toFixed(2)} USD` : '---'}
            </div>
            <div className="UserTotalPayments__Table__col">
              {periodRow.commissions ? `${periodRow.commissions.toFixed(2)} USD` : '---'}
            </div>
            <div className="UserTotalPayments__Table__col">{`${(periodRow.bonuses || 0).toFixed(2)} USD`}</div>
          </div>
        )}
        <style jsx>{staticStyles}</style>
        <style jsx>{dynamicStyles}</style>
      </div>
    </PageContentPreloader>
  );
};

UserTotalPayments.propTypes = {
  getUserTotalPayments: PropTypes.func.isRequired,
  totalPayments: PropTypes.object,
  theme: PropTypes.object,
  loadingTotalPayments: PropTypes.bool,
};

UserTotalPayments.defaultProps = {
  totalPayments: {},
  theme: {},
  loadingTotalPayments: true,
};

export default compose(
  withTheme(),
  connect(
    state => ({
      totalPayments: state.backoffice.users.totalPayments,
      loadingTotalPayments: state.backoffice.users.loadingTotalPayments,
    }),
    {
      getUserTotalPayments: (id, search) => getUserTotalPayments.request(id, search),
    }
  )
)(UserTotalPayments);
